<template>
  <div class="table-partner-cell">
    <BittsCheckbox
      v-if="canSelectRows"
      :checked="isSelected"
      :disabled="!isSelectable"
      :aria-label="selectCheckboxLabel"
      @input="handleCheckboxChange"
      @click.stop
      class="mr-8"
    />
    <BittsAvatar
      :org="{
        domain: partner.domain,
        name: partner.name,
        logo_url: partner.logo_url,
      }"
      size="small"
      :show-border="true"
    />
    <div class="table-partner-cell__info">
      <div class="table-partner-cell__name-row">
        <OverflowTooltip
          :text="partner.name"
          text-class="table-partner-cell__name"
        />
        <span
          v-if="partner.badgeText"
          class="table-partner-cell__source"
          data-testid="partner-source-badge"
        >
          {{ partner.badgeText }}
        </span>
      </div>
      <OverflowTooltip
        :text="partner.domain ?? ''"
        text-class="table-partner-cell__domain"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsCheckbox } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import OverflowTooltip from '@/components/shared/OverflowTooltip.vue';

import type { TableCellPartner } from './types';

const { params } = defineProps<{
  params: ICellRendererParams;
}>();

const partner = computed(() => params.value as TableCellPartner);
const selectCheckboxLabel = computed(() => `Select ${partner.value.name}`);
const canSelectRows = computed(
  () => params.context?.canSelectRows?.() ?? false,
);
const isSelectable = computed(() => params.node.selectable ?? true);
const isSelected = ref(params.node.isSelected() ?? false);

function onSelectionChanged() {
  isSelected.value = params.node.isSelected() ?? false;
}

onMounted(() => {
  params.node.addEventListener('rowSelected', onSelectionChanged);
});

onBeforeUnmount(() => {
  params.node.removeEventListener('rowSelected', onSelectionChanged);
});

function handleCheckboxChange(checked: boolean) {
  params.node.setSelected(checked);
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-partner-cell {
  @apply flex items-center gap-10 h-full w-full;
}
.table-partner-cell__info {
  @apply flex flex-col justify-center min-w-0;
}
.table-partner-cell__name-row {
  @apply flex items-center gap-6;
}
.table-partner-cell__name {
  @apply text-sm font-semibold text-neutral-text-strong truncate;
}
.table-partner-cell__source {
  @apply text-sm text-neutral-text whitespace-nowrap leading-none px-6 py-2 rounded-full bg-neutral-background shrink-0;
}
.table-partner-cell__domain {
  @apply text-sm text-neutral-text-weak truncate;
}
</style>
